import React, { useState, useEffect } from "react";
import { useAuth } from "../../Auth/AuthPage.jsx";
import { Paper, Typography, TextField, Button, List, ListItem, ListItemText, Alert, IconButton, Box, Grid, Link } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { Crumbs } from "../../Crumbs.jsx";
import LoginRequired from "../../LoginRequired.jsx";
import { InstagramEmbed } from "react-social-media-embed";

const BACKEND_HOST = process.env["REACT_APP_BACKEND_HOST"];

const AccountInstagramPage = () => {
    // Helper to check if form is valid
    const isFormValid = () => {
        if (!form.title.trim()) return false;
        if (!form.link.trim()) return false;
        const instaPattern = /^https:\/\/(www\.)?instagram\.com\//i;
        if (!instaPattern.test(form.link.trim())) return false;
        return true;
    };
    const { user, token, hasUserProfile } = useAuth();
    const [posts, setPosts] = useState([]);
    const [form, setForm] = useState({ title: "", link: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const loadPosts = () => {
        if (user && token) {
            fetch(`${BACKEND_HOST}/user-instagram-posts/mine`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
                .then(res => res.json())
                .then(data => setPosts(data || []))
                .catch(() => setError("Failed to load Instagram posts"));
        }
    }

    useEffect(() => {
        loadPosts()
    });

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            setError("Instagram post title is required.");
            return;
        }
        if (!form.link.trim()) {
            setError("Instagram post link is required.");
            return;
        }
        // Validate Instagram link
        const instaPattern = /^https:\/\/(www\.)?instagram\.com\//i;
        if (!instaPattern.test(form.link.trim())) {
            setError("Instagram post link must be an instagram.com link.");
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${BACKEND_HOST}/user-instagram-posts`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    title: form.title,
                    link: form.link
                }),
            });
            if (res.status !== 200) throw new Error("Failed to add Instagram post");
            loadPosts();
            setError("")
            setForm({ title: "", link: "" });
            setSuccess("Instagram post added!");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (documentId) => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            const res = await fetch(`${BACKEND_HOST}/user-instagram-posts/${documentId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (res.status !== 200) throw new Error("Failed to delete Instagram post");
            loadPosts();
            setSuccess("Instagram post deleted!");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
console.debug(posts)
    return (
        <Box sx={{ mt: 2 }}>
            <Crumbs crumbs={
                [{ title: "Community", href: "/community" },
                { title: "Instagram Posts", href: "/community/instagram-posts" },
                { title: "My Instagram Posts" }
                ]
            } />
            <LoginRequired>
                <Grid container spacing={2}>
                    <Grid item xs={12} sx={{ height: "100%" }}>
                        <Box py={3}>
                            <Typography variant="h4" align="center" gutterBottom>
                                Your Instagram Posts
                            </Typography>
                            <Typography variant="body2" align="center" gutterBottom>
                                Got a great Instagram post? Share it with the community!
                            </Typography>
                            <Box fullWidth textAlign="center" pt={3}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    sx={{ mb: 2 }}
                                    href="/community/instagram-posts">
                                    View Community Instagram Posts
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                        <Paper style={{ padding: 20, margin: "auto", height: "100%" }}>
                            <Typography variant="h6" align="center" gutterBottom>
                                Add an Instagram Post
                            </Typography>
                            <Typography variant="subtitle2" color={"text.secondary"} gutterBottom>
                                {hasUserProfile ? "Your posts will include your public profile. This can be adjusted in your profile settings." : "Your posts will be public, but will not be attributed to you since you do not have a public profile."}
                            </Typography>
                            <form onSubmit={handleSubmit} style={{ marginBottom: 20 }}>
                                <TextField
                                    label="Instagram Post Title"
                                    name="title"
                                    value={form.title}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="Instagram Link"
                                    name="link"
                                    value={form.link}
                                    onChange={handleChange}
                                    fullWidth
                                    margin="normal"
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading || !isFormValid() || !hasUserProfile}
                                    fullWidth
                                >
                                    {loading ? "Adding..." : "Add Instagram Post"}
                                </Button>
                                {!hasUserProfile && (
                                    <Alert severity="info" style={{ marginTop: 10 }}>
                                        You must create a public profile before adding an Instagram post.
                                        See your account page at <Link href="/account">/account</Link> to add a public profile.
                                    </Alert>
                                )}
                                {error && <Alert severity="error" style={{ marginTop: 10 }}>{error}</Alert>}
                                {success && <Alert severity="success" style={{ marginTop: 10 }}>{success}</Alert>}
                            </form>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                        <Box style={{ top: -25, position: "relative", height: "100%" }}>
                            <List>
                                {posts.map((post) => (
                                    <ListItem
                                        key={post.id}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(post.documentId)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        }>
                                        <ListItemText
                                            primary={post.title}
                                            secondary={<InstagramEmbed url={post.link} width={328} />}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Grid>
                </Grid>
            </LoginRequired>
        </Box>
    );
};

export default AccountInstagramPage;